import { HttpResult, some, none } from 'widgets-for-react'
import {
  HomePage,
  GenericPage,
  Link,
  KnowledgeArticlePage,
  DossierPage,
  BasePage,
  Page,
  CourseCategoryPage,
  CourseGroupPage,
  EventPage,
  ActualityPage,
  CasePage,
  CoursePage,
  ContactPerson,
  ArticlePage
} from '../../cmsPageState'
import {
  HomePageData,
  GenericPageData,
  KnowledgeArticlePageData,
  DossierPageData,
  BasePageData,
  SharepointPageData,
  CourseCategoryPageData,
  CourseGroupPageData,
  EventPageData,
  Tag,
  ActualityPageData,
  CasePageData,
  CoursePageData,
  ContactPersonData,
  ArticlePageData
} from '../types/pageData'
import { rerouteSPUrls } from './blockDataToBlock'
import { initialMembershipCalculatorState } from '../../../membershipCalculator/membershipCaclulatorState'
import { parseVideo } from '../../../../utils/video'

export const transformHomePageData = (data: HomePageData): HomePage => ({
  ...transformBasePageData(data),
  kind: 'home'
})

export const transformGenericPageData = (
  data: GenericPageData
): GenericPage => {
  return {
    ...transformBasePageData(data),
    kind: 'generic',
    parent: data.Parent ? some(transformGenericPageData(data.Parent)) : none(),
    menuOpened: false,
    membershipCalculatorState:
      data.Path === '/lidmaatschap'
        ? initialMembershipCalculatorState
        : undefined
  }
}

export function transformBasePageData<T extends BasePageData>(
  data: T
): BasePage {
  return {
    title: data.HeaderTitle || data.Title,
    metaTitle: data.MetaTitle,
    intro: data.HeaderDescription,
    image: data.HeaderImage,
    video: parseVideo(rerouteSPUrls(data.HeaderVideoUrl)),
    primaryButton: {
      title: data.PrimaryButton?.Title,
      slug: data.PrimaryButton?.Url
    },
    secondaryButton: {
      title: data.SecondaryButton?.Title,
      slug: data.SecondaryButton?.Url
    },
    slug: data.Path.replace(/^\/+/g, ''),
    path: data.AbsolutePath
  }
}

export const transformKnowledgeArticlePageData = (
  data: KnowledgeArticlePageData
): KnowledgeArticlePage => {
  return {
    ...transformBasePageData(data),
    kind: 'knowledge-article',
    primaryDossier:
      !!data.PrimaryDossierListItem && !!data.PrimaryDossierListItem.DossierPage
        ? some(
            transformDossierPageData(data.PrimaryDossierListItem.DossierPage)
          )
        : none(),
    secondaryDossiers: !!data.SecondaryDossierListItems
      ? some(
          data.SecondaryDossierListItems.filter(x => !!x.DossierPage).map(sd =>
            transformDossierPageData(sd.DossierPage)
          )
        )
      : none(),
    contactPerson: data.ContactPerson
      ? some(transformContactPersonData(data.ContactPerson))
      : none()
  }
}

export const transformCaseDetailPageData = (data: CasePageData): CasePage => {
  return {
    ...transformBasePageData(data),
    kind: 'case-detail',
    contactPerson: data.ContactPerson
      ? some(transformContactPersonData(data.ContactPerson))
      : none()
  }
}

export const transformEventPageData = (data: EventPageData): EventPage => {
  return {
    ...transformBasePageData(data),
    kind: 'event',
    eventBoostLabel: data.EventBoostLabel,
    eventDate: new Date(data.EventDate),
    eventLocation: data.EventLocation,
    eventMemberPrice: data.EventMemberPrice,
    eventNonMemberPrice: data.EventNonMemberPrice,
    eventTime: data.EventTime,
    eventFormId: data.EventFormId,
    Bijeenkomst: data.Bijeenkomst
  }
}

export const transformArticlePageData = (
  data: ArticlePageData
): ArticlePage => {
  return {
    ...transformBasePageData(data),
    kind: 'article',
    articleRelevance:
      data.ArticleRelevance === null ? undefined : data.ArticleRelevance,
    articleDate: new Date(data.ArticleDate),
    articleEditor: data.ArticleEditor,
    photographer: data.Photographer,
    magazine: data.Magazine === 'Logisticx' ? 'Logisticx' : 'Globe'
  }
}

export const transformDossierPageData = (
  data: DossierPageData
): DossierPage => {
  return {
    ...transformBasePageData(data),
    kind: 'dossier',
    primaryDossier:
      data.ParentDossierListItem && !!data.ParentDossierListItem.DossierPage
        ? some(transformDossierPageData(data.ParentDossierListItem.DossierPage))
        : none(),
    contactPerson: data.ContactPerson
      ? some(transformContactPersonData(data.ContactPerson))
      : none(),
    sharePointId: data.SharepointId
  }
}

export const transformContactPersonData = (
  data: ContactPersonData
): ContactPerson => {
  return {
    ...data,
    kind: 'contact-person',
    ImageUrl: !!data.ImageUrl
      ? {
          ...data.ImageUrl,
          Url: rerouteSPUrls(data.ImageUrl.Url)
        }
      : null
  }
}

export const transformCourseGroupPageData = (
  data: CourseGroupPageData
): CourseGroupPage => {
  return {
    ...transformBasePageData(data),
    kind: 'course-group',
    primaryCategory:
      !!data.PrimaryCourseCategoryListItem &&
      !!data.PrimaryCourseCategoryListItem.CourseCategoryPage
        ? some(
            transformCourseCategoryPageData(
              data.PrimaryCourseCategoryListItem.CourseCategoryPage
            )
          )
        : none(),
    secondaryCategories: data.SecondaryCourseCategoryListItems?.filter(
      x => !!x.CourseCategoryPage
    )?.map(category =>
      transformCourseCategoryPageData(category.CourseCategoryPage)
    ),
    sharePointId: data.SharepointId
  }
}

export const transformCourseCategoryPageData = (
  data: CourseCategoryPageData
): CourseCategoryPage => {
  return {
    ...transformBasePageData(data),
    kind: 'course-category',
    sharePointId: data.SharepointId
  }
}

export const transformCourseDetailsPageData = (data: CoursePageData): CoursePage => {
  return {
    ...transformBasePageData(data),
    kind: 'course-details',
    group: !!data.CourseGroupListItem && !!data.CourseGroupListItem.CourseGroupPage ? some(transformCourseGroupPageData(data.CourseGroupListItem.CourseGroupPage)) : none(),
    courseInCompany: data.CourseInCompany,
    courseHideAvailableSeats: data.CourseHideAvailableSeats,
    courseHideSchedule: data.CourseHideSchedule,
    courseStartDateAndLocationsIntroduction: data.CourseStartDateAndLocationsIntroduction,
    courseDurationOverride: data.CourseDurationOverride,
    Evenement: data.Evenement
  }
}

export const transformActualityDetailsPageData = (
  data: ActualityPageData
): ActualityPage => {
  return {
    ...transformBasePageData(data),
    kind: 'actuality-details',
    sharePointId: data.SharepointId,
    tags: data.tags ? data.tags?.map((t: Tag) => ({ title: t.Title })) : [],
    primaryDossier:
      !!data.PrimaryDossierListItem && !!data.PrimaryDossierListItem.DossierPage
        ? some(
            transformDossierPageData(data.PrimaryDossierListItem.DossierPage)
          )
        : none(),
    secondaryDossiers: !!data.SecondaryDossierListItems
      ? some(
          data.SecondaryDossierListItems.filter(x => !!x.DossierPage).map(x =>
            transformDossierPageData(x.DossierPage)
          )
        )
      : none()
  }
}
// [Custom content types]: Add a function to transform your api response model into your page model here

export type PreviewSharepointPageData =
  | HomePageData
  | DossierPageData
  | GenericPageData
  | KnowledgeArticlePageData
  | EventPageData
  | CourseCategoryPageData
  | CourseGroupPageData
  | CoursePageData
  | ActualityPageData
  | CasePageData
  | ArticlePageData

export const transformSharepointPageData = (x: SharepointPageData): Page => {
  switch (x.ContentType) {
    case 'Homepage':
      return transformHomePageData(x)
    case 'Dossier page':
      return transformDossierPageData(x)
    case 'Evofenedex page':
      return transformGenericPageData(x)
    case 'Knowledge article page':
      return transformKnowledgeArticlePageData(x)
    case 'Event page':
      return transformEventPageData(x)
    case 'Article page':
      return transformArticlePageData(x)
    case 'Course category page':
      return transformCourseCategoryPageData(x)
    case 'Course group page':
      return transformCourseGroupPageData(x)
    case 'Course page':
      return transformCourseDetailsPageData(x)
    case 'Actuality page':
      return transformActualityDetailsPageData(x)
    case 'Case page':
      return transformCaseDetailPageData(x)
  }
}

// [Custom content types]: Add case here
